import { MetricConfig } from '@tableau/metrics-engine';
import { configToProto,protoToB64 } from '@tableau/metrics-shared-widgets-dom';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { MarkViewedRequest, ViewedObjectType } from '~src/spacesPlatform/serverApi/requests/MarkViewedRequest';

import { Messages } from '../../codegen/Localize';
import { IDetailFetcher } from '../../httpClient/fetchers/IDetailFetcher';
import { IListFetcher } from '../../httpClient/fetchers/IListFetcher';
import { WebClientDetailFetcher } from '../../httpClient/fetchers/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '../../httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { FieldValuesQuery } from '../../httpClient/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '../../httpClient/resources/Resource';
import { ResourceQuery } from '../../httpClient/resources/ResourceQuery';
import { GetFieldValuesRequest } from '../../httpClient/serverApi/requests/GetFieldValuesRequest';
import { IFieldValue } from '../../httpClient/serverApi/types/IFieldValue';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToast, WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../../recents/requests/MarkRecentlyViewedRequest';
import { IMetricInfo } from '../models/IMetricInfo';
import { MetricMapper } from '../models/mappers/MetricMapper';
import { AddTagsToMetricsRequest } from '../serverApi/requests/AddTagsToMetricsRequest';
import { DeleteMetricsRequest } from '../serverApi/requests/DeleteMetricsRequest';
import { GetMetricActionsRequest } from '../serverApi/requests/GetMetricActionsRequest';
import { GetMetricsRequest } from '../serverApi/requests/GetMetricsRequest';
import { RemoveTagsFromMetricsRequest } from '../serverApi/requests/RemoveTagsFromMetricsRequest';
import { RenameMetricRequest } from '../serverApi/requests/RenameMetricRequest';
import { IMetricDescriptionParams, SetMetricDescriptionRequest } from '../serverApi/requests/SetMetricDescriptionRequest';
import { SetMetricsOwnerRequest } from '../serverApi/requests/SetMetricsOwnerRequest';
import { SetMetricsSuspendedStateRequest } from '../serverApi/requests/SetMetricsSuspendedStateRequest';
import { UpdateMetricConfigurationRequest } from '../serverApi/requests/UpdateMetricConfigurationRequest';
import { IMetricActions } from '../serverApi/types/IMetricActions';
import { sendMetricConfigurationEditedEvent } from '../telemetry/MetricsTelemetry';

class MetricsResource implements IListResource<IMetricInfo>, IFieldValuesResource, IActionResource<IMetricActions>, IDetailResource<IMetricInfo> {
  private listFetcher: IListFetcher<IMetricInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;
  private detailFetcher: IDetailFetcher<IMetricInfo>;

  constructor() {
    this.listFetcher = new WebClientListResourceFetcher(GetMetricsRequest, ServerApiMapper.metrics);
    this.fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
    this.detailFetcher = new WebClientDetailFetcher('getMetric', MetricMapper.mapDetail);
  }

  public all(): ResourceQuery<IMetricInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'metric');
  }

  public getById(id: string): Promise<IMetricInfo> {
    return this.detailFetcher.fetchById(id);
  }

  private getErrorNotifyingServer(): WebHttpClientWithErrorToast {
    return WebHttpClientWithErrorToastInstance;
  }

  // Actions

  public actions(id: string): Promise<IMetricActions> {
    return WebHttpClient.sendRequest(new GetMetricActionsRequest(id));
  }

  public del(ids: string[]): Promise<IResult> {
    return this.getErrorNotifyingServer().sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteMetricsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setOwner(ids: string[], ownerId: string): Promise<IResult> {
    return this.getErrorNotifyingServer().sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetMetricsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public setMetricDescription = async (args: IMetricDescriptionParams): Promise<IResult> => {
    return this.getErrorNotifyingServer()
      .sendRequestAndDelay(new SetMetricDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessful(), Messages.editDescriptionFailure()));
  };

  public rename(id: string, name: string): Promise<IResult> {
    return this.getErrorNotifyingServer().sendRequestAndDelay(new RenameMetricRequest(id, name));
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? this.getErrorNotifyingServer().sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new AddTagsToMetricsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? this.getErrorNotifyingServer().sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new RemoveTagsFromMetricsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public updateConfiguration(metricLuid: string,
    newConfig: MetricConfig,
    oldConfig: MetricConfig): Promise<IResult> {
    return this.getErrorNotifyingServer()
      .sendRequest(new UpdateMetricConfigurationRequest({metricLuid, metricConfiguration: protoToB64(configToProto(newConfig))}))
      .then(r => {
        sendMetricConfigurationEditedEvent(
          metricLuid,
          JSON.stringify(newConfig),
          JSON.stringify(oldConfig),
          !!r && (!r.errors || r.errors.length === 0));
        return this.toast(r, Messages.metricEditConfigurationSuccess(), Messages.metricEditConfigurationFailure());}
      );
  }

  public isMetricInfo(item: any): item is IMetricInfo {
    return item &&
           item.contentType &&
           item.contentType === 'metric' &&
           item.hasOwnProperty('connectedView');
  }

  public markRecentlyViewed(id: string): Promise<IResult> {
    return WebHttpClient.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.Metric
    }));
  }

  public markViewed(id: string): Promise<IResult> {
    return WebHttpClient.sendRequest(new MarkViewedRequest({
      objectId: id,
      objectType: ViewedObjectType.Metric
    }));
  }

  public resumeMetrics(ids: string[]): Promise<IResult> {
    return WebHttpClient.sendRequest(new SetMetricsSuspendedStateRequest(false, ids));
  }

  public toast(result: IResult, successMessage: string, failureMessage: string): any {
    if (result.errors && result.errors.length > 0) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: failureMessage
      });
    } else {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: successMessage
      });
    }
  }
}

export const Metrics = new MetricsResource();
